import { lazy, memo, useCallback, useState, useMemo } from 'react';

import * as ModalWindows from '@Stores/ModalWindows';
import useMedias from '@Hooks/useMedias';
import Trigger from './elems/Trigger';

import type { OptionData } from './typings';
import type { MouseEvent, FC, HTMLAttributes } from 'react';

export interface DropdownProps extends HTMLAttributes<HTMLDivElement> {
  label: string;
  options?: OptionData[];
  onCheck?: (e: MouseEvent, option: OptionData) => void;
}

const OptionsPopup = lazy(() => import('./elems/OptionsPopup'));

const Dropdown: FC<DropdownProps> = (props) => {
  const { label, options, onCheck, ...restProps } = props;
  const { isMobile } = useMedias();
  const [opened, setOpened] = useState(false);

  const selectedOption = useMemo(() => {
    return options.find((option) => option.selected);
  }, [options]);

  const handleClose = useCallback(() => {
    setOpened(false);

    if (isMobile) {
      ModalWindows.close('MobileOptions');
    }
  }, [isMobile]);

  const handleCheck = useCallback(
    (e: MouseEvent, option: OptionData) => {
      if (onCheck) onCheck(e, option);
      handleClose();
    },
    [handleClose, onCheck],
  );

  const handleOpen = useCallback(() => {
    setOpened(true);

    if (isMobile) {
      ModalWindows.open(
        'MobileOptions',
        {
          label,
          options,
          onClose: handleClose,
          onCheck: handleCheck,
        },
        { needToReplace: false },
      );
    }
  }, [handleCheck, handleClose, isMobile, label, options]);

  return (
    <Trigger
      {...restProps}
      label={selectedOption ? selectedOption.name : label}
      opened={opened}
      onOpen={handleOpen}
      onClose={handleClose}
    >
      {!isMobile && <OptionsPopup label={label} options={options} onCheck={handleCheck} />}
    </Trigger>
  );
};

export default memo(Dropdown);
